/**
 * The browser's held `beforeinstallprompt`, and the one way to spend it.
 *
 * Two surfaces offer the install now — the dashboard invitation and Perfil's Seguridad
 * tab — and both need the same three facts: whether a native prompt exists, how to open
 * it, and what she answered. Without this hook each would re-derive the subscription and
 * the clear-after-use rule, and the two would drift the first time one of them changed.
 *
 * Module-scope store, not component state: `root.tsx` unmounts the PWA provider on the
 * player, and a `useState` event died with it. `install-prompt-store.ts` carries the full
 * account of that.
 */
import { useRef, useState, useSyncExternalStore } from "react";
import {
  clearInstallPrompt,
  getInstallPrompt,
  getServerInstallPrompt,
  subscribeToInstallPrompt,
  type BeforeInstallPromptEvent,
} from "~/lib/pwa/install-prompt-store";

export interface InstallPromptHandle {
  /** The held event, or `null` — which is every iPhone, and any browser that has not offered one. */
  deferredPrompt: BeforeInstallPromptEvent | null;
  installing: boolean;
  /**
   * Opens the native dialog and returns what she chose, or `null` when there was no
   * prompt to open. Spends the event either way: a `beforeinstallprompt` may be used once.
   */
  promptInstall: () => Promise<"accepted" | "dismissed" | null>;
}

export function useInstallPrompt(): InstallPromptHandle {
  const [installing, setInstalling] = useState(false);
  const inFlight = useRef(false);
  const deferredPrompt = useSyncExternalStore(
    subscribeToInstallPrompt,
    getInstallPrompt,
    getServerInstallPrompt,
  );

  async function promptInstall(): Promise<"accepted" | "dismissed" | null> {
    if (!deferredPrompt || inFlight.current) return null;
    inFlight.current = true;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      return outcome;
    } catch {
      // A consumed/rejected native prompt falls back to the existing manual steps.
      return null;
    } finally {
      clearInstallPrompt();
      inFlight.current = false;
      setInstalling(false);
    }
  }

  return { deferredPrompt, promptInstall, installing };
}
